import { InjectionToken } from '@angular/core';
import { AppGlyphKey } from '@/shared/components/app-glyph/app-glyph.component';

/**
 * App registry: the single source of truth for the four Envolve Hub apps
 * (SCRUM-589, masthead epic story 2).
 *
 * The sidebar model (story 7) and the app masthead (stories 4/6) are both
 * derived from this constant; the app routes in `apps/apps.routes.ts` carry
 * `data.app: AppKey` so the layout can look an app up by route.
 *
 * Names and purpose lines follow the approved design boards
 * (`docs/design/app-headers/boards/N8-Combined.dc.html`). Links are absolute
 * app URLs and must resolve to real routes (`app-registry.spec.ts` checks this).
 *
 * Role / WM-team gating fields have the same meaning as on the sidebar items:
 * see `isMenuItemVisible` in `core/layout/menu-visibility.ts`.
 *
 * The Administration area (`/admin/*`) has a registry of the same shape,
 * `ADMIN_REGISTRY` in `admin-registry.ts` (groups and pages instead of apps
 * and sections; SCRUM-619), which feeds the same masthead in its admin tone.
 */

/** The four apps: every glyph key except the Administration gear. */
export type AppKey = Exclude<AppGlyphKey, 'admin'>;

export interface AppSection {
  key: string;
  label: string;
  /** Absolute URL of the section's landing page. */
  link: string;
  /** URL path prefixes (no query/fragment) that count as this section. Defaults to [link]. */
  activeWhen?: readonly string[];
  requiredRoles?: readonly string[];
}

export interface AppDefinition {
  key: AppKey;
  name: string;
  purpose: string;
  glyph: AppKey;
  /** Sidebar target. */
  home: string;
  /** URL prefixes that belong to the app. */
  activeWhen: readonly string[];
  /** `sections[0]` is the default section. */
  sections: readonly AppSection[];
  /** Roles required to see the app (ANY of these unless `requireAllRoles`). */
  requiredRoles?: readonly string[];
  requireAllRoles?: boolean;
  /** Data gate: needs a WM-enabled team (System/Org admins exempt). */
  requiresWmEnabledTeam?: boolean;
}

/** Shape of `data` on the four app routes in `apps/apps.routes.ts`. */
export interface AppRouteData {
  breadcrumb: string;
  app: AppKey;
}

const OM_PREFIX = '/apps/opportunity-management';
const GEMBA_PREFIX = '/apps/gemba';
const WM_PREFIX = '/apps/workspace-management';
const DASH_PREFIX = '/apps/analytics';

/** Sidebar order: om, gemba, wm, dash. */
export const APP_REGISTRY: readonly AppDefinition[] = [
  {
    key: 'om',
    name: 'Opportunity Management',
    purpose: 'Capture, prioritise and resolve improvement opportunities.',
    glyph: 'om',
    home: `${OM_PREFIX}/in-progress`,
    activeWhen: [OM_PREFIX],
    sections: [
      {
        key: 'in-progress',
        label: 'In Progress',
        link: `${OM_PREFIX}/in-progress`,
        // Create/detail pages for opportunities and tasks default to In Progress.
        activeWhen: [`${OM_PREFIX}/in-progress`, `${OM_PREFIX}/opportunity/`, `${OM_PREFIX}/task/`],
      },
      { key: 'resolved', label: 'Resolved', link: `${OM_PREFIX}/resolved` },
      { key: 'my-submissions', label: 'My Submissions', link: `${OM_PREFIX}/my-submissions` },
    ],
  },
  {
    key: 'gemba',
    name: 'Gemba',
    purpose: 'Plan, walk and follow up on the shop floor.',
    glyph: 'gemba',
    home: GEMBA_PREFIX,
    activeWhen: [GEMBA_PREFIX],
    sections: [
      {
        key: 'scheduled',
        label: 'Scheduled Walks',
        link: GEMBA_PREFIX,
        activeWhen: [GEMBA_PREFIX, `${GEMBA_PREFIX}/walks/create`],
      },
      { key: 'completed', label: 'Completed Walks', link: `${GEMBA_PREFIX}/completed` },
    ],
  },
  {
    key: 'wm',
    name: 'Workspace Management',
    purpose: '5S+ checklists and audits for every team area.',
    glyph: 'wm',
    home: WM_PREFIX,
    activeWhen: [WM_PREFIX],
    // Mirrors the sidebar item: no role requirement, but hidden unless the
    // user has a WM-enabled team (System/Org admins always see it).
    requiresWmEnabledTeam: true,
    sections: [
      {
        key: 'checklists',
        label: 'Checklists',
        link: WM_PREFIX,
        activeWhen: [WM_PREFIX, `${WM_PREFIX}/checklists/`, `${WM_PREFIX}/audits/`],
      },
      { key: 'history', label: 'History', link: `${WM_PREFIX}/history` },
    ],
  },
  {
    key: 'dash',
    name: 'Dashboards',
    purpose: 'Cross-team performance at a glance.',
    glyph: 'dash',
    home: `${DASH_PREFIX}/opportunity`,
    activeWhen: [DASH_PREFIX],
    // Gemba / 5S+ dashboards are added when those pages exist.
    sections: [{ key: 'om', label: 'Opportunity Management', link: `${DASH_PREFIX}/opportunity` }],
  },
];

/**
 * Injectable view of {@link APP_REGISTRY} so the masthead container (story 6)
 * can be fed a stub registry in specs. Production code never overrides it.
 */
export const APP_REGISTRY_TOKEN = new InjectionToken<readonly AppDefinition[]>('APP_REGISTRY', {
  providedIn: 'root',
  factory: () => APP_REGISTRY,
});

export function findApp(key: AppKey, registry: readonly AppDefinition[] = APP_REGISTRY): AppDefinition {
  const app = registry.find((a) => a.key === key);
  if (!app) {
    throw new Error(`Unknown app key: ${key}`);
  }
  return app;
}
